import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, fmtErr, fmtDateTime, fmtSize } from "@/lib/api";
import { StatusBadge, PriorityBadge } from "@/components/badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PlusCircle, Paperclip } from "lucide-react";
import { motion } from "framer-motion";

export function ClientHomePage() {
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    api.get("/stats/dashboard").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/tickets").then(({ data }) => setTickets(data)).catch((e) => toast.error(fmtErr(e)));
  }, []);

  return (
    <div data-testid="client-home-page">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Mes demandes</h1>
          <p className="mt-1 text-sm text-muted-foreground">Suivez vos demandes et échangez avec les équipes.</p>
        </div>
        <Link to="/portal/nouvelle-demande" data-testid="client-new-ticket-button"
          className="inline-flex items-center gap-2 rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 transition-colors duration-200">
          <PlusCircle className="h-4 w-4" /> Nouvelle demande
        </Link>
      </div>

      <div className="mb-8 grid grid-cols-3 gap-4">
        <div className="rounded-sm border border-border bg-card p-5" data-testid="client-kpi-open">
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">En cours</div>
          <div className="mt-2 font-heading text-3xl font-semibold text-primary">{stats?.tickets_open ?? "—"}</div>
        </div>
        <div className="rounded-sm border border-border bg-card p-5" data-testid="client-kpi-closed">
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Clôturés</div>
          <div className="mt-2 font-heading text-3xl font-semibold">{stats?.tickets_closed ?? "—"}</div>
        </div>
        <div className="rounded-sm border border-border bg-card p-5" data-testid="client-kpi-total">
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Total</div>
          <div className="mt-2 font-heading text-3xl font-semibold">{stats?.tickets_total ?? "—"}</div>
        </div>
      </div>

      <div className="rounded-sm border border-border bg-card" data-testid="client-tickets-list">
        {tickets.map((t, i) => (
          <motion.div key={t.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
            <Link to={`/portal/tickets/${t.id}`} data-testid={`client-ticket-${t.number}`}
              className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-4 last:border-b-0 hover:bg-accent transition-colors duration-200">
              <span className="font-mono text-xs text-muted-foreground">{t.number}</span>
              <span className="min-w-0 flex-1 basis-full break-words text-sm font-medium sm:basis-auto sm:truncate">{t.subject}</span>
              {t.category_name && (
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="h-2 w-2 rounded-full" style={{ background: t.category_color || "#666" }} />{t.category_name}
                </span>
              )}
              <StatusBadge status={t.status} />
              <PriorityBadge priority={t.priority} />
              <span className="text-xs text-muted-foreground">{fmtDateTime(t.last_message_at)}</span>
            </Link>
          </motion.div>
        ))}
        {tickets.length === 0 && (
          <div className="p-10 text-center text-sm text-muted-foreground">
            Aucune demande pour le moment. Créez votre première demande pour démarrer.
          </div>
        )}
      </div>
    </div>
  );
}

export function NewTicketPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ subject: "", description: "", category_id: "" });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileInput = useRef(null);

  const pickFiles = (list) => {
    let arr = Array.from(list || []);
    const tooBig = arr.filter((f) => f.size > 5 * 1024 * 1024);
    if (tooBig.length) {
      toast.error("Chaque fichier doit faire moins de 5 Mo.");
      arr = arr.filter((f) => f.size <= 5 * 1024 * 1024);
    }
    if (arr.length > 15) {
      toast.error("Vous pouvez joindre jusqu'à 15 fichiers maximum par message.");
      arr = arr.slice(0, 15);
    }
    setFiles(arr);
  };

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: ticket } = await api.post("/tickets", {
        subject: form.subject, description: form.description,
        category_id: form.category_id || null,
      });
      if (files.length) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        await api.post(`/tickets/${ticket.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Demande créée — vous recevrez une notification (simulation)");
      navigate(`/portal/tickets/${ticket.id}`);
    } catch (err) {
      toast.error(fmtErr(err));
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl" data-testid="new-ticket-page">
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Nouvelle demande</h1>
      <p className="mt-1 text-sm text-muted-foreground">Décrivez votre besoin : une équipe est notifiée immédiatement.</p>

      <form onSubmit={submit} className="mt-8 space-y-5 rounded-sm border border-border bg-card p-6">
        <div className="space-y-2">
          <Label>Catégorie</Label>
          <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="new-ticket-category-select" className="rounded-sm"><SelectValue placeholder="Choisir une catégorie…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucune / je ne sais pas</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`new-ticket-category-${c.id}`}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Sujet *</Label>
          <Input data-testid="new-ticket-subject-input" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
            className="rounded-sm" placeholder="Ex : Sol non nettoyé dans le hall" />
        </div>
        <div className="space-y-2">
          <Label>Description *</Label>
          <Textarea data-testid="new-ticket-description-input" required rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="rounded-sm" placeholder="Décrivez précisément la situation, le lieu, l'urgence…" />
        </div>
        <div className="space-y-2">
          <Label>Photos / documents (max 15, 5 Mo chacun)</Label>
          <div>
            <input ref={fileInput} type="file" multiple hidden accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx"
              data-testid="new-ticket-file-input" onChange={(e) => pickFiles(e.target.files)} />
            <Button type="button" variant="outline" data-testid="new-ticket-attach-button" onClick={() => fileInput.current?.click()} className="rounded-sm">
              <Paperclip className="mr-2 h-4 w-4" /> Ajouter des fichiers ({files.length}/15)
            </Button>
          </div>
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {files.map((f, i) => <span key={i} className="rounded-sm border border-border bg-muted px-2 py-1 text-xs">{f.name} ({fmtSize(f.size)})</span>)}
            </div>
          )}
        </div>
        <Button data-testid="new-ticket-submit-button" type="submit" disabled={saving} className="w-full rounded-sm bg-primary text-white hover:bg-red-700">
          {saving ? "Envoi…" : "Envoyer ma demande"}
        </Button>
      </form>
    </div>
  );
}
